import type { BadgeStyle } from '@renewal/types';
import { BADGE_STYLE_KEYS } from '@renewal/types';

/**
 * Badge preset palette (unification, Task 6). The backend stores only the
 * `badge_style` key (a `BadgeStyle`); the glyph + color live here in the
 * frontend so the legend and pickers render a consistent swatch. A task code
 * with no `badgeStyle` falls back to the legend's hash→HSL color.
 */
export interface BadgePreset {
  /** The persisted key. */
  key: BadgeStyle;
  /** Human label for the picker. */
  label: string;
  /** Emoji glyph rendered on the badge. */
  glyph: string;
  /** Background color (the legend computes contrasting text). */
  color: string;
}

export const BADGE_PRESETS: Record<BadgeStyle, BadgePreset> = {
  farm: { key: 'farm', label: 'Farm', glyph: '🚜', color: '#2e7d32' }, // green
  housing: { key: 'housing', label: 'Housing', glyph: '🏠', color: '#5c6bc0' }, // slate-blue
  school: { key: 'school', label: 'School', glyph: '🎓', color: '#3949ab' }, // indigo
  volunteer: { key: 'volunteer', label: 'Volunteer', glyph: '🤝', color: '#00897b' }, // teal
  general: { key: 'general', label: 'General', glyph: '👽', color: '#1e88e5' }, // blue
};

/** Presets in display order (for the picker). */
export const BADGE_PRESET_LIST: readonly BadgePreset[] = BADGE_STYLE_KEYS.map(
  (k) => BADGE_PRESETS[k]
);

/** Resolve a stored key to its preset, or null when unset/unknown. */
export function badgePresetFor(badgeStyle: string | null | undefined): BadgePreset | null {
  if (!badgeStyle) return null;
  return (BADGE_PRESETS as Record<string, BadgePreset>)[badgeStyle] ?? null;
}
